import { useLocation } from "@/lib/router-compat";
import { useSEO } from '@/hooks/useSEO';

const SITE = 'https://getamorette.com';

interface RouteMeta {
  title: string;
  description: string;
  noindex?: boolean;
  /** Overrides the canonical path when several URLs serve the same page. */
  canonicalPath?: string;
}

/**
 * Route-level metadata for public pages that do not set their own SEO tags.
 * Pages with their own useSEO() call are intentionally absent from this map so
 * there is exactly one source of metadata per route.
 */
const ROUTE_META: Record<string, RouteMeta> = {
  '/': {
    title: 'Amorette | Curated Dinner Experiences for Real Connection',
    description:
      'Amorette brings a handful of thoughtfully matched people together over dinner. No swiping, no endless messaging — one curated table at a time.',
  },
  '/try': {
    title: 'Amorette | Dinner First, Connection Second',
    description:
      'See how Amorette replaces swiping with curated dinner experiences in your city. Verified members, small tables, real conversation.',
  },
  '/pricing': {
    title: 'Membership & Pricing | Amorette',
    description:
      'Amorette membership options and seat reservations explained — what is included, how reservations work, and what each tier unlocks.',
  },
  '/learn-more': {
    title: 'How Amorette Works | Curated Dinners, Not Swiping',
    description:
      'Learn how Amorette matches verified members into small dinner groups, how venues are curated, and what to expect at your first table.',
  },
  '/about': {
    title: 'About Amorette | Why We Built Dinner-First Connection',
    description:
      'The story behind Amorette: a dinner-first alternative to dating apps built around verified members, curated venues, and small tables.',
  },
  '/contact': {
    title: 'Contact Amorette | Support, Press & Partnerships',
    description:
      'Reach the Amorette team for member support, billing questions, venue partnerships, press enquiries, or concierge requests.',
  },
  '/faq': {
    title: 'Frequently Asked Questions | Amorette',
    description:
      'Answers about Amorette membership, reservations, verification, venues, cancellations, safety, and how curated dinners work.',
  },
  '/app': {
    title: 'Get the Amorette App | Curated Dinner Experiences',
    description:
      'Download Amorette to manage your reservations, connections, and curated dinner experiences from your phone.',
  },
  '/first-500': {
    title: 'Founding 500 | Amorette Launch Waitlist',
    description:
      'Be one of the first 500 verified people to join the Amorette launch waitlist in your city. Verify your email to secure your place.',
  },
  '/careers': {
    title: 'Careers at Amorette | Build Dinner-First Connection',
    description:
      'Open roles at Amorette. Help build a curated, dinner-first alternative to dating apps across new launch markets.',
  },
  '/become-curator': {
    title: 'Become an Amorette Curator | Host Curated Dinners',
    description:
      'Apply to become an Amorette curator and help host curated dinner experiences in your city.',
  },
  '/become-affiliate': {
    title: 'Amorette Affiliate Program | Partner With Us',
    description:
      'Join the Amorette affiliate program and introduce your audience to curated dinner experiences.',
  },
  '/partner': {
    title: 'Partner With Amorette | Restaurants & Venues',
    description:
      'Bring curated Amorette dinner experiences to your restaurant or venue. Learn how venue partnerships work.',
  },
  '/partner-with-us': {
    title: 'Partnership Programs | Amorette',
    description:
      'Explore Amorette partnership programs for venues, curators, affiliates, and brand collaborations.',
  },
  '/partner-apply': {
    title: 'Venue Partner Application | Amorette',
    description:
      'Apply to host curated Amorette dinner experiences at your restaurant or venue.',
  },
  '/partners': {
    title: 'Partner Venues | Amorette Restaurant & Venue Collaborations',
    description:
      'Premium restaurants, rooftops, and lounges selected to host curated Amorette dining experiences.',
  },
  '/offers': {
    title: 'Member Offers | Amorette',
    description: 'Current Amorette member offers and add-ons available in approved launch markets.',
  },

  // Account, checkout, and portal routes — functional pages, never indexed.
  '/auth': { title: 'Sign In or Create Your Amorette Account', description: 'Sign in to Amorette or create an account to join curated dinner experiences.', noindex: true },
  '/signin': { title: 'Sign In | Amorette', description: 'Sign in to your Amorette account.', noindex: true },
  '/signup': { title: 'Create Your Amorette Account', description: 'Create an Amorette account to join curated dinner experiences.', noindex: true },
  '/reset-password': { title: 'Reset Your Password | Amorette', description: 'Set a new password for your Amorette account.', noindex: true },
  '/verify-email': { title: 'Verify Your Email | Amorette', description: 'Confirm your email address to finish setting up your Amorette account.', noindex: true },
  '/email-status': { title: 'Email Status | Amorette', description: 'Check the delivery status of your Amorette verification email.', noindex: true },
  '/onboarding': { title: 'Set Up Your Profile | Amorette', description: 'Complete your Amorette profile to be matched into curated dinners.', noindex: true },
  '/verify-profile': { title: 'Verify Your Identity | Amorette', description: 'Complete identity verification to join Amorette dinner experiences.', noindex: true },
  '/verification-complete': { title: 'Verification Complete | Amorette', description: 'Your Amorette identity verification is complete.', noindex: true },
  '/choose-plan': { title: 'Choose Your Membership | Amorette', description: 'Select the Amorette membership that fits how you want to dine.', noindex: true },
  '/waitlist': { title: 'You Are on the Amorette Waitlist', description: 'Your Amorette waitlist status and next steps.', noindex: true },
  '/sms-opt-in': { title: 'Text Message Reminders | Amorette', description: 'Manage text reminders for your Amorette reservations.', noindex: true },
  '/payment/confirm': { title: 'Payment Confirmation | Amorette', description: 'Your Amorette payment confirmation.', noindex: true },
  '/concierge-submitted': { title: 'Concierge Request Received | Amorette', description: 'Your Amorette concierge request has been received.', noindex: true },
  '/concierge/packages': { title: 'Concierge Packages | Amorette', description: 'Private concierge experience options prepared for you.', noindex: true },
  '/unsubscribe': { title: 'Unsubscribe | Amorette', description: 'Manage your Amorette email preferences.', noindex: true },
  '/email-unsubscribe': { title: 'Unsubscribe | Amorette', description: 'Manage your Amorette email preferences.', noindex: true },
  '/marketing-unsubscribe': { title: 'Unsubscribe | Amorette', description: 'Stop receiving Amorette marketing emails.', noindex: true },
  '/first-500/success': { title: 'You Are In | Amorette Founding 500', description: 'Your Amorette Founding 500 confirmation.', noindex: true },
  '/first-500/cancel': { title: 'Checkout Cancelled | Amorette', description: 'Your Amorette checkout was cancelled.', noindex: true },
  '/discover': { title: 'Discover Dinners | Amorette', description: 'Your curated Amorette dinner experiences.', noindex: true },
  '/dashboard': { title: 'Your Dashboard | Amorette', description: 'Your Amorette dashboard.', noindex: true },
  '/reservations': { title: 'Your Reservations | Amorette', description: 'Manage your Amorette reservations.', noindex: true },
  '/connections': { title: 'Your Connections | Amorette', description: 'People you connected with at Amorette dinners.', noindex: true },
  '/messages': { title: 'Messages | Amorette', description: 'Your Amorette conversations.', noindex: true },
  '/circles': { title: 'Circles | Amorette', description: 'Your Amorette circles.', noindex: true },
  '/profile': { title: 'Your Profile | Amorette', description: 'Manage your Amorette profile.', noindex: true },
  '/leaderboard': { title: 'Leaderboard | Amorette', description: 'Amorette community leaderboard.', noindex: true },
  '/membership': { title: 'Your Membership | Amorette', description: 'Manage your Amorette membership.', noindex: true },
  '/settings': { title: 'Settings | Amorette', description: 'Manage your Amorette account settings.', noindex: true },
  '/venue-portal': { title: 'Venue Partner Portal | Amorette', description: 'Amorette venue partner portal.', noindex: true },
  '/affiliate-portal': { title: 'Affiliate Portal | Amorette', description: 'Amorette affiliate portal.', noindex: true },
  '/curator-portal': { title: 'Curator Portal | Amorette', description: 'Amorette curator portal.', noindex: true },
  '/partner/login': { title: 'Partner Sign In | Amorette', description: 'Sign in to your Amorette partner portal.', noindex: true },
  '/partner/activate': { title: 'Activate Your Partner Account | Amorette', description: 'Activate your Amorette partner portal account.', noindex: true },
  '/admin/login': { title: 'Administrator Sign In | Amorette', description: 'Amorette administrator sign in.', noindex: true },
  '/admin-invite': { title: 'Accept Your Invitation | Amorette', description: 'Accept your Amorette team invitation.', noindex: true },
  '/invite': { title: 'Accept Your Invitation | Amorette', description: 'Accept your Amorette team invitation.', noindex: true },
  '/trust-verification': { title: 'Trust & Verification | Amorette', description: 'How Amorette verifies members and protects the community.' },

  // Legal pages — canonical single URLs, one effective date each.
  '/terms': {
    title: 'Terms of Service | Amorette',
    description: 'The Amorette Terms of Service governing membership, reservations, conduct, payments, and cancellations. Effective September 22, 2026.',
  },
  '/privacy': {
    title: 'Privacy Policy | Amorette',
    description: 'How Amorette collects, uses, protects, and retains your personal information, and the privacy choices available to you. Effective September 22, 2026.',
  },
  '/community-guidelines': {
    title: 'Community Guidelines | Amorette',
    description: 'The standards of conduct expected of every Amorette member at curated dinner experiences and in messages.',
  },
  '/safety': {
    title: 'Safety at Amorette | Verification & Member Protection',
    description: 'How Amorette keeps members safe: identity verification, curated venues, reporting tools, and moderation.',
  },
  '/cancellation-policy': {
    title: 'Cancellation Policy | Amorette',
    description: 'Amorette cancellation windows, attendance expectations, and how reservation changes are handled.',
  },
  '/policies/cancellation': {
    title: 'Cancellation Policy | Amorette',
    description: 'Amorette cancellation windows, attendance expectations, and how reservation changes are handled.',
    canonicalPath: '/cancellation-policy',
  },
};

const FALLBACK: RouteMeta = {
  title: 'Amorette',
  description: 'Amorette — curated dinner experiences for real connection.',
  noindex: true,
};

const ApplyMeta = ({ meta, path }: { meta: RouteMeta; path: string }) => {
  useSEO({
    title: meta.title,
    description: meta.description,
    canonicalUrl: meta.noindex ? undefined : `${SITE}${meta.canonicalPath ?? path}`,
    noindex: meta.noindex ?? false,
    ogType: 'website',
  });
  return null;
};

/**
 * Applies per-route titles, descriptions, canonical URLs, and indexing
 * directives for routes that do not manage their own metadata. Routes that
 * already call useSEO() themselves are deliberately absent from the map and
 * are left untouched here.
 */
export const RouteSEO = () => {
  const { pathname } = useLocation();
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : '/';
  const isInternal =
    normalized.startsWith('/admin') ||
    normalized.startsWith('/checkin') ||
    normalized.startsWith('/host/');
  const meta = ROUTE_META[normalized] ?? (isInternal ? FALLBACK : null);

  if (!meta) return null;
  return <ApplyMeta meta={meta} path={normalized} />;
};

export default RouteSEO;
